export type OpenReplayTracker = {
  start: () => void;
  configure?: (config: Record<string, unknown>) => void;
  use?: (plugin: unknown) => void;
  setUserID?: (userId: string) => void;
  setMetadata?: (key: string, value: string) => void;
  event?: (name: string, payload?: any) => void;
  handleError?: (e: any, metadata?: Record<string, any>) => void;
};

import log from "logbeacon";

declare global {
  interface Window {
    __OR_TRACKER__?: OpenReplayTracker | null;
    __OR_TRACKER_READY__?: Promise<OpenReplayTracker | null> | null;
  }
}

export function getOrCreateClientUuid(): string {
  // Use the project's canonical uuid source (logbeacon).
  // NOTE: current codebase exposes `log.getOrCreateUUID()` (used for fingerprint init).
  return (log as any).getOrCreateClientUuid?.() ?? log.getOrCreateUUID();
}

export function getTracker(): OpenReplayTracker | null {
  return window.__OR_TRACKER__ ?? null;
}

export async function ensureOpenReplayTrackerStarted(): Promise<OpenReplayTracker | null> {
  if (typeof window === "undefined") {
    return null;
  }

  const existing = getTracker();
  if (existing) {
    return existing;
  }

  if (window.__OR_TRACKER_READY__) {
    return window.__OR_TRACKER_READY__;
  }

  window.__OR_TRACKER_READY__ = (async () => {
    try {
      const projectKey = process.env.NEXT_PUBLIC_OPENREPLAY_PROJECT_KEY;
      const ingestPoint = process.env.NEXT_PUBLIC_OPENREPLAY_INGEST_POINT;
      if (!projectKey) {
        return null;
      }

      const [tracker, trackerAssist] = await Promise.all([
        import("@openreplay/tracker").then(
          (m) => m.tracker as unknown as OpenReplayTracker,
        ),
        import("@openreplay/tracker-assist").then((m) => m.default),
      ]);

      tracker.configure?.({
        projectKey: projectKey,
        ingestPoint: ingestPoint,
        __DISABLE_SECURE_MODE: process.env.NODE_ENV !== "production",
      });

      try {
        tracker.use?.((trackerAssist as any)({}));
      } catch {
        // ignore plugin failures
      }

      tracker.start?.();
      window.__OR_TRACKER__ = tracker;

      // Anonymous user continuity (before login).
      try {
        const clientUuid = getOrCreateClientUuid();
        tracker.setUserID?.(clientUuid);
      } catch {
        // ignore
      }

      // Error reporting (best-effort)
      window.addEventListener("error", (e) => {
        (tracker as any).handleError?.(e);
      });
      window.addEventListener("unhandledrejection", (e) => {
        (tracker as any).handleError?.(e);
      });

      return tracker;
    } catch {
      return null;
    }
  })();

  return window.__OR_TRACKER_READY__;
}

/**
 * Best-effort OpenReplay custom event (shows up in OpenReplay analytics).
 * Safe to call before tracker is ready.
 */
export function emitOpenReplayEvent(
  name: string,
  payload?: Record<string, unknown>,
): void {
  if (typeof window === "undefined") {
    return;
  }
  try {
    getTracker()?.event?.(name, payload);
  } catch {
    // ignore
  }
}

/**
 * Builds OpenReplay DOM label v2: `<Scene>: <Domain>: <Action>: <OriginalText>`
 * @see apps/token-seek/docs/openreplay-events-report.md
 */
export function buildOpenReplayLabel(
  scene: string,
  domain: string,
  action: string,
  originalText: string,
): string {
  const ot = originalText.replace(/\s+/g, " ").trim() || "Action";
  return `${scene}: ${domain}: ${action}: ${ot}`;
}
